import { useState, useEffect, useRef } from 'react';
import {
  Mail, Phone, MapPin, Instagram, Facebook, Twitter, Youtube,
  Linkedin, Music, Calendar, ExternalLink, Play, ChevronRight, ChevronLeft, Star, Quote,
  User, ArrowRight, X, Plus, Briefcase, Users, HelpCircle, MessageSquare, CheckCircle,
  Pause
} from 'lucide-react';

import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { AnimatedSection } from './AnimatedSection';

const THEMES = {
  dark_stage: {
    bg: 'bg-slate-900',
    text: 'text-white',
    textMuted: 'text-slate-400',
    card: 'bg-slate-800',
    border: 'border-slate-700',
    accent: 'text-orange-500',
    button: 'bg-orange-500 hover:bg-orange-400',
  },
  theatre_red: {
    bg: 'bg-[#1a0c0e] tx-velvet',
    text: 'text-[#f8f0e6]',
    textMuted: 'text-[#bfa39a]',
    card: 'bg-[#120709] tx-velvet-alt',
    border: 'border-[#3b2125]',
    accent: 'text-[#d4af37]',
    button: 'bg-[#b91c1c] hover:bg-[#dc2626]',
  },
  ivory_gold: {
    bg: 'bg-[#faf7f1]',
    text: 'text-[#1c1710]',
    textMuted: 'text-[#8d7f68]',
    card: 'bg-[#f1ebdd]',
    border: 'border-[#e2d8c3]',
    accent: 'text-[#b08d2f]',
    button: 'bg-[#1c1710] hover:bg-[#3a2f1e]',
  },
  light_modern: {
    bg: 'bg-white',
    text: 'text-slate-900',
    textMuted: 'text-slate-500',
    card: 'bg-slate-50',
    border: 'border-slate-200',
    accent: 'text-blue-600',
    button: 'bg-blue-600 hover:bg-blue-500',
  },
  colorful_kids: {
    bg: 'bg-amber-50',
    text: 'text-purple-900',
    textMuted: 'text-purple-600',
    card: 'bg-white',
    border: 'border-pink-200',
    accent: 'text-pink-500',
    button: 'bg-pink-500 hover:bg-pink-400',
  },
  elegant_classic: {
    bg: 'bg-stone-900',
    text: 'text-stone-50',
    textMuted: 'text-stone-400',
    card: 'bg-stone-800',
    border: 'border-stone-700',
    accent: 'text-amber-400',
    button: 'bg-amber-500 hover:bg-amber-400',
  },
  bold_artist: {
    bg: 'bg-zinc-900',
    text: 'text-white',
    textMuted: 'text-zinc-400',
    card: 'bg-zinc-800',
    border: 'border-zinc-700',
    accent: 'text-red-500',
    button: 'bg-red-500 hover:bg-red-400',
  },
};

// Social icon mapper
const getSocialIcon = (platform) => {
  const name = platform.toLowerCase();
  if (name.includes('instagram')) return Instagram;
  if (name.includes('facebook')) return Facebook;
  if (name.includes('twitter') || name.includes('x')) return Twitter;
  if (name.includes('youtube')) return Youtube;
  if (name.includes('linkedin')) return Linkedin;
  if (name.includes('spotify') || name.includes('music')) return Music;
  return ExternalLink;
};


export const FAQSection = ({ data, theme, primaryColor }) => {
  const t = THEMES[theme] || THEMES.dark_stage;
  const faqs = data?.faqs || [];
  const [openIdx, setOpenIdx] = useState(null);
  
  if (faqs.length === 0) return null;

  return (
    <section id="faq" className={cn("py-24 px-4", t.card)}>
      <div className="max-w-3xl mx-auto">
        <AnimatedSection animation="fadeUp" className="text-center mb-16">
          <span className="text-sm uppercase tracking-widest font-semibold mb-4 block" style={{ color: primaryColor }}>Got Questions?</span>
          <h2 className={cn("text-4xl md:text-5xl font-bold", t.text)} style={{ fontFamily: "'Outfit', sans-serif" }}>{data.title || 'FAQ'}</h2>
        </AnimatedSection>
        
        <div className="space-y-4">
          {faqs.map((faq, idx) => (
            <AnimatedSection
              key={faq.id || idx}
              animation="fadeUp"
              delay={idx * 80}
            >
              <button
                onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
                className={cn(
                  "w-full text-left p-6 rounded-xl border transition-all",
                  openIdx === idx ? "border-orange-500" : t.border,
                  t.bg
                )}
              >
                <div className="flex items-center justify-between">
                  <h3 className={cn("font-semibold pr-4", t.text)}>{faq.question}</h3>
                  <div 
                    className={cn("w-8 h-8 rounded-full flex items-center justify-center transition-transform", openIdx === idx && "rotate-45")}
                    style={{ backgroundColor: primaryColor + '20', color: primaryColor }}
                  >
                    <Plus className="w-5 h-5" />
                  </div>
                </div>
                <div className={cn(
                  "overflow-hidden transition-all duration-300",
                  openIdx === idx ? "max-h-96 mt-4" : "max-h-0"
                )}>
                  <p className={t.textMuted}>{faq.answer}</p>
                </div>
              </button>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};


